import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useCallback } from 'react';

import {
  APPROVE_DOCS_BLOCK_ID,
  DOC_TARGET_BLOCK_ID,
  getApproveDocBlockId,
  getDocTargetBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmDataCreated,
  getKnmErknmAcceptedDocumentsSelectOptions,
  getKnmFormRef,
  getKnmIsEditableInStatusCommentsExist,
  getPermitDocsSelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ErknmFillInProcessMessage } from 'KnmErknmPackage/MainModule/components/ErknmFillInProcessMessage';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ErknmApproveRemarkAddButton } from 'KnmErknmPackage/ErknmApproveRemarkModule/components/ErknmApproveRemarkAddButton';

import { IPermitDocs } from '../../../APIPackage/DictionariesModule/interfaces/IPermitDocs';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { IKnmErknmFormDocTarget } from '../../MainModule/interfaces/IKnmErknmFormDocTarget';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { requiredRequirementsErknmOrApproveDocsOrLinks } from '../../../CorePackage/MainModule/validators/requiredRequirementsErknmOrApproveDocsOrLinks';
import { IKnmErknmFormApproveDoc } from '../../MainModule/interfaces/IKnmErknmFormApproveDoc';
import { CloseButton } from '../../../UIPackage/MainModule/components/CloseButton';
import { IAcceptedDocument } from '../../../APIPackage/DictionariesModule/interfaces/IAcceptedDocument';
import { KnmApproveDocs } from '../../../KnmPackage/KnmEventSubjectModule/components/KnmApproveDocs';
import {
  getDocTargetOptions,
  getDocTargetType,
} from '../../../KnmPackage/KnmEventSubjectModule/common/docTypeGetFunctions';
import { ErknmFieldMark } from '../../MainModule/components/ErknmFieldMark';
import { ErknmApproveRemarkSectionCodeEnum } from '../../ErknmApproveRemarkModule/enums/ErknmApproveRemarkSectionCodeEnum';
import { ErknmApproveRemark } from '../../ErknmApproveRemarkModule/components/ErknmApproveRemark';
import { useIsApproveDocsEnabled } from '../../MainModule/fieldsAvailability/erknmApproveDocsAvailability';

interface IErknmApproveDocsStoreProps {
  formRef: IKnmFormRef | undefined;
  isAvailableInStatusCommentsExist: boolean;
  settings: IAdminSetting[];
  docOptionsOld: IDictionarySelectOption<IAcceptedDocument>[] | undefined;
  docOptions: IDictionarySelectOption<IPermitDocs>[] | undefined;
  created: string | null | undefined;
}

const ErknmApproveDocsWithoutStore = ({
  formRef,
  docOptions,
  docOptionsOld,
  isAvailableInStatusCommentsExist,
  settings,
  created,
}: IErknmApproveDocsStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isEnabled = useIsApproveDocsEnabled(formRef);

  /**
   * Добавление нового элемента
   */
  const addField = useCallback(() => {
    if (isEnabled && push) {
      const formDoc: IKnmErknmFormDocTarget = {
        ervkControlKindDocTarget: undefined,
        specificRequirementAndClause: undefined,
        file: undefined,
        listId: uuidv4(),
        docTargetType: getDocTargetType(docOptions),
        guid: undefined,
      };

      const docTarget = formRef.current.getFieldState('docTarget')?.value;
      const selectedFields = docTarget?.map(field => field?.ervkControlKindDocTarget?.targetTitle) || [];

      const options = getDocTargetOptions(selectedFields, formDoc, docOptions);

      formDoc.ervkControlKindDocTarget = options?.length === 1 ? options[0] : undefined;

      push('docTarget', formDoc);
    }
  }, [isEnabled, push, docOptions, formRef]);

  /**
   * Добавление нового элемента (старая версия)
   */
  const addFieldOld = useCallback(() => {
    if (isEnabled && push) {
      const addingField: IKnmErknmFormApproveDoc = {
        approveDoc: undefined,
        dateCreate: undefined,
        numGuid: undefined,
        listId: uuidv4(),
      };

      push('approveDocs', addingField);
    }
  }, [isEnabled, push]);

  const makeRemoveFieldOld = (index: number) => () => isEnabled && remove && remove('approveDocs', index);

  const isShow22 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM);

  const isEnabledBy481415 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PERMITTED_DOC_481_4_1_5_STARTS_FROM,
  );

  if (isShow22 && isEnabledBy481415 && docOptions && docOptions.length > 0) {
    return (
      <FieldBlock
        label={
          <>
            Разрешительные и иные документы
            <ErknmFieldMark fieldName="docTarget" />
          </>
        }
        id={DOC_TARGET_BLOCK_ID}
        button={
          <>
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              disabled={!isEnabled}
              onClick={addField}
            />
            <ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.APPROVE_DOCS} />
          </>
        }
        fillInProcess={<ErknmFillInProcessMessage name="docTarget" />}
      >
        <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.APPROVE_DOCS} />
        <FieldArray name="docTarget" key={isAvailableInStatusCommentsExist ? 0 : 1}>
          {({ fields }: FieldArrayRenderProps<IKnmErknmFormDocTarget, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return (
              <Row>
                {fields.map((fieldName, sIndex) => {
                  const fieldValue: IKnmErknmFormDocTarget = fields.value?.[sIndex];
                  const id = getDocTargetBlockId(fieldValue.listId);
                  const selectedFields = fields.value.map(field => field?.ervkControlKindDocTarget?.targetTitle);

                  return (
                    <KnmApproveDocs
                      id={id}
                      isWatchOnly={!isEnabled}
                      docOptions={docOptions}
                      fieldName={fieldName}
                      fieldValue={fieldValue}
                      selectedFields={selectedFields}
                      sIndex={sIndex}
                      key={id}
                    />
                  );
                })}
              </Row>
            );
          }}
        </FieldArray>
      </FieldBlock>
    );
  }

  if (!isShow22) {
    return (
      <FieldBlock
        label="Разрешительные и иные документы"
        id={APPROVE_DOCS_BLOCK_ID}
        button={
          <>
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              disabled={!isEnabled}
              onClick={addFieldOld}
            />
            <ErknmApproveRemarkAddButton sectionCode={ErknmApproveRemarkSectionCodeEnum.APPROVE_DOCS} />
          </>
        }
        fillInProcess={<ErknmFillInProcessMessage name="approveDocs" />}
      >
        <ErknmApproveRemark sectionCode={ErknmApproveRemarkSectionCodeEnum.APPROVE_DOCS} />
        <FieldArray
          name="approveDocs"
          validate={isAvailableInStatusCommentsExist ? requiredRequirementsErknmOrApproveDocsOrLinks : () => undefined}
          key={isAvailableInStatusCommentsExist ? 0 : 1}
        >
          {({ fields }: FieldArrayRenderProps<IKnmErknmFormApproveDoc, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return (
              <Row>
                {fields.map((fieldName, sIndex) => {
                  const fieldValue: IKnmErknmFormApproveDoc = fields.value?.[sIndex];
                  const id = getApproveDocBlockId(fieldValue.listId);

                  return (
                    <Row key={id} margin="medium" isListItem>
                      <SelectInputField
                        className="shared-deleting-row"
                        id={`${fieldName}.approveDoc`}
                        name={`${fieldName}.approveDoc`}
                        placeholder="Выберите документ"
                        options={docOptionsOld}
                        isClearable={false}
                        size="medium"
                        disabled={!isEnabled}
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                      <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveFieldOld(sIndex)} />
                    </Row>
                  );
                })}
              </Row>
            );
          }}
        </FieldArray>
      </FieldBlock>
    );
  }

  return <></>;
};

const mapStateToProps = (state: IRootState): IErknmApproveDocsStoreProps => ({
  formRef: getKnmFormRef(state),
  isAvailableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
  settings: getAdminFetchSettings(state),
  docOptionsOld: getKnmErknmAcceptedDocumentsSelectOptions(state),
  docOptions: getPermitDocsSelectOptions(state),
  created: getKnmDataCreated(state),
});

export const ErknmApproveDocs = connect(mapStateToProps)(ErknmApproveDocsWithoutStore);
